import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import type { LoginResponse, TotpResponse } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { FormField } from '../components/FormField.tsx';

type Audience = 'agent' | 'client';
type Step = 'credentials' | 'totp';

export function LoginPage(): React.ReactElement {
  const { setAgent, agent, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromState = (location.state as { from?: string } | null)?.from;

  // Default to the Client tab: client portal contacts vastly outnumber
  // technicians, and clients landing on the agent login (which has no
  // account for them) was the cause of widespread "MFA won't let me in"
  // reports — every attempt failed with "Email or password is incorrect"
  // before TOTP was ever reached. Technicians switch to the Technician tab.
  const [audience, setAudience] = useState<Audience>('client');
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (agent) {
    navigate(fromState ?? '/dashboard', { replace: true });
    return <></>;
  }

  const loginPath = audience === 'agent' ? '/api/auth/agent/login' : '/api/portal/auth/login';
  const totpPath = audience === 'agent' ? '/api/auth/agent/totp' : '/api/portal/auth/totp';
  // Only return someone to the page they were sent here from if it belongs to
  // the side they signed in as. A client whose browser opens a technician
  // page (a bookmark, an autocompleted /dashboard) is bounced here with that
  // page remembered; sending them back to it after a correct code bounces
  // them to /login again with no error, which reads as "MFA fails silently".
  const fromPortal = fromState?.startsWith('/portal') ?? false;
  const successRedirect =
    audience === 'agent'
      ? (fromState && !fromPortal ? fromState : '/dashboard')
      : (fromPortal ? fromState! : '/portal');

  function switchAudience(next: Audience) {
    setAudience(next);
    setStep('credentials');
    setError(null);
    setEmail('');
    setPassword('');
    setCode('');
  }

  async function submitCredentials(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await apiFetch<LoginResponse>(loginPath, {
        method: 'POST',
        json: { email, password },
      });
      if (res.status === 'totp_required') {
        setStep('totp');
        setCode('');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in failed.');
    } finally {
      setBusy(false);
    }
  }

  async function submitTotp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (audience === 'agent') {
        const res = await apiFetch<TotpResponse>(totpPath, {
          method: 'POST',
          json: { code: code.trim() },
        });
        setAgent(res.agent, res.remainingBackupCodes);
        navigate(successRedirect, { replace: true });
      } else {
        await apiFetch(totpPath, { method: 'POST', json: { code: code.trim() } });
        await refresh();
        navigate(successRedirect, { replace: true });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Verification failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-md">
      <div className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-8 shadow-sm">
        <div className="mb-6 inline-flex w-full overflow-hidden rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white">
          <button
            type="button"
            onClick={() => switchAudience('agent')}
            className={
              'flex-1 px-4 py-2 text-sm font-medium transition ' +
              (audience === 'agent'
                ? 'bg-[var(--color-t3-blue)] text-white'
                : 'text-[var(--color-body)] hover:bg-[var(--color-surface)]')
            }
          >
            Technician
          </button>
          <button
            type="button"
            onClick={() => switchAudience('client')}
            className={
              'flex-1 px-4 py-2 text-sm font-medium transition ' +
              (audience === 'client'
                ? 'bg-[var(--color-t3-blue)] text-white'
                : 'text-[var(--color-body)] hover:bg-[var(--color-surface)]')
            }
          >
            Client
          </button>
        </div>

        <h1 className="text-2xl font-bold text-[var(--color-header)]">
          {audience === 'agent' ? 'Technician sign in' : 'Client portal sign in'}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          {step === 'credentials'
            ? `Enter your ${audience === 'agent' ? 'T3' : ''} email and password.`.replace('  ', ' ')
            : 'Enter the 6-digit code from your authenticator app, or a backup code.'}
        </p>

        {step === 'credentials' ? (
          <form onSubmit={submitCredentials} className="mt-6 space-y-4">
            <FormField
              label="Email"
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <FormField
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? 'Signing in…' : 'Continue'}
            </Button>
            <p className="text-center text-sm">
              <a
                href={`/forgot?audience=${audience === 'agent' ? 'agent' : 'client'}`}
                className="text-[var(--color-muted)] hover:underline"
              >
                Forgot password?
              </a>
            </p>
          </form>
        ) : (
          <form onSubmit={submitTotp} className="mt-6 space-y-4">
            <FormField
              label="Authentication code"
              name="code"
              autoComplete="one-time-code"
              inputMode="text"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              hint="6-digit code from your authenticator, or a backup code (XXXX-XXXX)."
            />
            {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? 'Verifying…' : 'Verify'}
            </Button>
            <button
              type="button"
              className="text-sm text-[var(--color-muted)] underline-offset-2 hover:text-[var(--color-t3-blue)] hover:underline"
              onClick={() => {
                setStep('credentials');
                setError(null);
                setCode('');
              }}
            >
              Back to email + password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
